import { useState } from "react";
import { IconScale } from "@tabler/icons-react";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import EmptyState from "../UI/EmptyState";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import MoisPicker from "./MoisPicker";
import { useBalanceComptable } from "../../hooks/use-compta-pro";
import type { TypeCompteComptable } from "../../models/comptabilite";
import {
  euros,
  periodeCourante,
  TYPE_COMPTE_LABELS,
  type Periode,
} from "./comptaUtils";
import styles from "./Compta.module.css";

interface TypeOption {
  value: TypeCompteComptable | "";
  label: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { value: "", label: "Tous les comptes" },
  ...(
    Object.entries(TYPE_COMPTE_LABELS) as [TypeCompteComptable, string][]
  ).map(([value, label]) => ({ value, label })),
];

export default function BalanceTab() {
  const [periode, setPeriode] = useState<Periode>(periodeCourante());
  const [typeFilter, setTypeFilter] = useState<TypeOption>(TYPE_OPTIONS[0]);
  const { data, isLoading } = useBalanceComptable(
    periode.year,
    periode.month,
    typeFilter.value || undefined,
  );

  const rows = data?.rows ?? [];

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <MoisPicker
          label="Situation à fin"
          className={styles.moisPicker}
          value={periode}
          onChange={setPeriode}
        />
        <ReactSelectStandalone
          label="Type de compte"
          className={styles.journalSelect}
          options={TYPE_OPTIONS}
          value={typeFilter}
          onChange={(option: TypeOption) => setTypeFilter(option)}
        />
      </div>
      <Text>
        Soldes cumulés depuis le début du premier mandat jusqu'à la fin du mois
        sélectionné.
      </Text>
      {data && data.anomalies.length > 0 && (
        <Callout variant="warning" title="Anomalies de projection">
          {data.anomalies.join(" — ")}
        </Callout>
      )}
      {isLoading ? (
        <IsLoading />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={IconScale}
          title="Aucun compte mouvementé"
          description="La balance se remplit dès que des écritures existent sur la plage."
        />
      ) : (
        <Table variant="card">
          <Table.Header>
            <Table.Column>Compte</Table.Column>
            <Table.Column>Libellé</Table.Column>
            <Table.Column>Type</Table.Column>
            <Table.Column align="right">Débit</Table.Column>
            <Table.Column align="right">Crédit</Table.Column>
            <Table.Column align="right">Solde</Table.Column>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row key={row.numero}>
                <Table.Cell>{row.numero}</Table.Cell>
                <Table.Cell>{row.label}</Table.Cell>
                <Table.Cell>
                  <Status
                    color="neutral"
                    text={TYPE_COMPTE_LABELS[row.type] ?? row.type}
                  />
                </Table.Cell>
                <Table.Cell align="right">{euros(row.total_debit)}</Table.Cell>
                <Table.Cell align="right">{euros(row.total_credit)}</Table.Cell>
                <Table.Cell align="right">
                  <Text
                    as="span"
                    color={parseFloat(row.solde) < 0 ? "danger" : undefined}
                  >
                    {euros(row.solde)}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
}
